import type { ReactNode } from "react";
import {
  Box,
  PageHeroBackdrop,
  PageHeroFrame,
  PageHeroStatus,
  PageHeroText,
  Stack,
  Text,
} from "@generalapplications/ui";

import { ResponsiveImage } from "@/app/common/components/composite/media/ResponsiveImage";
import { Reveal } from "@/app/common/components/composite/motion/Reveal";
import { formatText } from "@/app/common/functions/format/formatText";

/** `page`: a page's banner, on the height floor every page's shares, so the
 * top of the page stays put from one page to the next; `document`: a reading
 * document, such as the privacy policy, whose text starts right under its
 * title. */
export type HeroSize = "page" | "document";

export type PageHeroProps = {
  headline: string;
  /** The lead paragraph, then any that continue it at body size. */
  copy: readonly string[];
  /** The orientation label. Left out on a page whose breadcrumb already says
   * where the reader is: one orientation label per page. */
  eyebrow?: string;
  /** Beside the eyebrow: the page's lifecycle state, such as a draft chip. */
  status?: ReactNode;
  /** The art behind the hero, faded under the words. */
  image?: string;
  /** A control the hero presents, under the lead: a filter, an action. */
  children?: ReactNode;
  /** `page` (default) holds the height floor every page's banner shares;
   * `document` has none, for a reading document whose text starts right
   * under its title. */
  size?: HeroSize;
};

/**
 * The banner at the top of a page: the library's hero text in its frame,
 * over the page's art. The paragraphs carry inline marks (links, accented
 * words), so they go in as `Text`s rather than the plain-string lede.
 */
export function PageHero({
  headline,
  copy,
  eyebrow,
  status,
  image,
  children,
  size = "page",
}: PageHeroProps) {
  const [lead, ...trailing] = copy;

  return (
    <Reveal>
      <PageHeroFrame minHeight={size === "page" ? "page" : undefined}>
        {image && (
          <PageHeroBackdrop>
            <ResponsiveImage src={image} width={1200} height={630} eager />
          </PageHeroBackdrop>
        )}
        <PageHeroText eyebrow={eyebrow} title={formatText(headline)}>
          {status && <PageHeroStatus>{status}</PageHeroStatus>}
          <Box paddingTop="lg">
            <Stack gap="lg" align="start">
              {lead && <Text variant="lead">{formatText(lead)}</Text>}
              {children}
              {trailing.map((paragraph) => (
                <Text key={paragraph} variant="body">
                  {formatText(paragraph)}
                </Text>
              ))}
            </Stack>
          </Box>
        </PageHeroText>
      </PageHeroFrame>
    </Reveal>
  );
}
